import React, { useState, useEffect } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BreadcrumbManual from '../components/help/BreadcrumbManual';
import MarkdownRenderer from '../components/help/MarkdownRenderer';

const API = process.env.REACT_APP_API_URL || '';

export default function FaqManual() {
  const [contenido, setContenido] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/ayuda-content/faq.md`)
      .then(r => { if (!r.ok) throw new Error(); return r.text(); })
      .then(setContenido)
      .catch(() => setError('Las preguntas frecuentes aún no están disponibles.'))
      .finally(() => setCargando(false));
  }, []);

  const secciones = parsearSecciones(contenido);

  return (
    <Box sx={{ maxWidth: 680 }}>
      <BreadcrumbManual tituloArticulo="Preguntas frecuentes" />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 0.5 }}>
          ❓ Preguntas frecuentes
        </Typography>
        <Typography sx={{ color: '#90a4ae', fontSize: 14 }}>
          Respuestas a las dudas más comunes por rol.
        </Typography>
      </Box>

      {cargando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: 'var(--c-acento)' }} />
        </Box>
      )}
      {error && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ fontSize: 32, mb: 2 }}>🔧</Typography>
          <Typography sx={{ color: '#90a4ae', fontSize: 14 }}>{error}</Typography>
        </Box>
      )}
      {!cargando && !error && (
        secciones.length > 0 ? (
          secciones.map((sec, i) => (
            <Box key={i} sx={{ mb: 3 }}>
              {sec.titulo && (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#546e7a',
                  textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>
                  {sec.titulo}
                </Typography>
              )}
              {sec.items.map((item, j) => (
                <Accordion
                  key={j}
                  disableGutters
                  elevation={0}
                  sx={{
                    bgcolor: 'var(--c-superficie)', mb: 0.5, borderRadius: '8px !important',
                    border: '1px solid rgba(255,255,255,0.06)',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { borderColor: 'rgba(144,202,249,0.2)' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#546e7a', fontSize: 18 }} />}
                    sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: '10px' } }}
                  >
                    <Typography sx={{ fontSize: 14, color: '#e0e0e0', fontWeight: 500 }}>
                      {item.pregunta}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                    <MarkdownRenderer>{item.respuesta}</MarkdownRenderer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))
        ) : (
          <Box sx={{
            bgcolor: 'var(--c-superficie)', borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.06)', p: 3,
          }}>
            <MarkdownRenderer>{contenido}</MarkdownRenderer>
          </Box>
        )
      )}
    </Box>
  );
}

function parsearSecciones(texto) {
  if (!texto) return [];
  const lineas = texto.split('\n');
  const secciones = [];
  let secActual = { titulo: '', items: [] };
  let preguntaActual = '';
  let respuestaLineas = [];

  function cerrarPregunta() {
    if (preguntaActual) {
      secActual.items.push({ pregunta: preguntaActual, respuesta: respuestaLineas.join('\n').trim() });
      preguntaActual = '';
      respuestaLineas = [];
    }
  }

  function cerrarSeccion() {
    cerrarPregunta();
    if (secActual.titulo || secActual.items.length) secciones.push(secActual);
    secActual = { titulo: '', items: [] };
  }

  for (const linea of lineas) {
    if (linea.startsWith('## ')) {
      cerrarSeccion();
      secActual.titulo = linea.replace(/^## /, '').trim();
    } else if (linea.startsWith('### ') || (linea.startsWith('**') && linea.endsWith('**'))) {
      cerrarPregunta();
      preguntaActual = linea.replace(/^### /, '').replace(/^\*\*|\*\*$/g, '').trim();
    } else if (linea.startsWith('#### ')) {
      cerrarPregunta();
      preguntaActual = linea.replace(/^#### /, '').trim();
    } else if (preguntaActual) {
      respuestaLineas.push(linea);
    }
  }
  cerrarSeccion();
  return secciones;
}
